
import * as ENGINE from '@gnsx/genesys.js';
import { startCase } from 'es-toolkit';
import {
  BlocksIcon,
  BoneIcon,
  BoxIcon,
  DatabaseIcon,
  FileBoxIcon,
  FileCode2Icon,
  FileTextIcon,
  FilmIcon,
  FolderIcon,
  ImageIcon,
  MountainIcon,
  MusicIcon,
  PaletteIcon,
  Sparkles,
  SunIcon,
} from 'lucide-react';
import { IoMdCube as PrefabIcon } from 'react-icons/io';

import type { DragDropHandler } from '@/editor/core/dragAndDrop/dragAndDropHandler';
import type { IAssetFile } from '@/editor/store/models';
import type { FileItem } from '@gnsx/genesys.js';
import type { LucideIcon } from 'lucide-react';

import { JS_CLASSES_DIR_NAME, MESH_COMBINATION_FILE_EXT } from '@/const';

export const AssetType = {
  Folder: 'folder',
  Model: 'model',
  Texture: 'texture',
  HDRI: 'hdri',
  Video: 'video',
  Audio: 'audio',
  Json: 'json',
  Scene: 'scene',
  Prefab: 'prefab',
  Material: 'material',
  Resource: 'resource',
  SourceCode: 'sourcecode',
  JsClass: 'jsclass',
  MeshCombination: 'mesh-combination',
  NavMesh: 'navmesh',
  LightmapBuildData: 'lightmap-build-data',
  VFX: 'vfx',
  AnimationConfig: 'animconfig',
  SkeletonProfile: 'skeletonprofile',
  Voxel: 'voxel',
} as const;
export type AssetType = (typeof AssetType)[keyof typeof AssetType];

export const assetTypeDisplayNames: Record<AssetType, string> = {
  [AssetType.Folder]: 'Folder',
  [AssetType.Model]: 'Model',
  [AssetType.Texture]: 'Texture',
  [AssetType.HDRI]: 'HDRI',
  [AssetType.Video]: 'Video',
  [AssetType.Audio]: 'Audio',
  [AssetType.Json]: 'JSON',
  [AssetType.Scene]: 'Scene',
  [AssetType.Prefab]: 'Prefab',
  [AssetType.Material]: 'Material',
  [AssetType.Resource]: 'Resource',
  [AssetType.SourceCode]: 'Source Code',
  [AssetType.JsClass]: 'JS Class',
  [AssetType.MeshCombination]: 'Mesh Combination',
  [AssetType.NavMesh]: 'NavMesh',
  [AssetType.LightmapBuildData]: 'Lightmap Build Data',
  [AssetType.VFX]: 'VFX',
  [AssetType.AnimationConfig]: 'Animation Config',
  [AssetType.SkeletonProfile]: 'Skeleton Profile',
  [AssetType.Voxel]: 'Voxel',
} as const;

export function getAssetTypeDisplayName(type: AssetType): string {
  return assetTypeDisplayNames[type] ?? startCase(type);
}

/** Icon mapping for asset types - single source of truth for asset icons */
export const ASSET_ICONS: Record<AssetType, LucideIcon | typeof PrefabIcon> = {
  [AssetType.Folder]: FolderIcon,
  [AssetType.Model]: BoxIcon,
  [AssetType.Texture]: ImageIcon,
  [AssetType.HDRI]: ImageIcon,
  [AssetType.Video]: FilmIcon,
  [AssetType.Audio]: MusicIcon,
  [AssetType.Json]: FileTextIcon,
  [AssetType.Scene]: MountainIcon,
  [AssetType.Prefab]: PrefabIcon,
  [AssetType.Material]: PaletteIcon,
  [AssetType.Resource]: DatabaseIcon,
  [AssetType.SourceCode]: FileCode2Icon,
  [AssetType.JsClass]: FileBoxIcon,
  [AssetType.MeshCombination]: BlocksIcon,
  [AssetType.NavMesh]: FileTextIcon,
  [AssetType.LightmapBuildData]: SunIcon,
  [AssetType.VFX]: Sparkles,
  [AssetType.AnimationConfig]: FilmIcon,
  [AssetType.SkeletonProfile]: BoneIcon,
  [AssetType.Voxel]: BoxIcon,
} as const;

/** 3D mesh model file extensions (lowercase, no leading dot). */
export const MODEL_FILE_EXTENSIONS = [
  'glb', 'gltf', 'fbx',
  'usd', 'usda', 'usdc', 'usdz',
] as const;

const MODEL_FILE_EXTENSION_SET = new Set<string>(MODEL_FILE_EXTENSIONS);

/** Strip a model file extension from a display name. */
export const MODEL_FILE_NAME_PATTERN = /\.(glb|gltf|fbx|usd|usda|usdc|usdz)$/i;

export function isModelFileExtension(extension: string): boolean {
  return MODEL_FILE_EXTENSION_SET.has(extension.toLowerCase());
}

export function isModelAssetPath(assetPath: string): boolean {
  const lowerPath = assetPath.toLowerCase();
  return MODEL_FILE_EXTENSIONS.some(ext => lowerPath.endsWith(`.${ext}`));
}

/** Supported file extensions for import */
/** Supported file extensions for import, kept in sync with getAssetType logic and engine constants */
export const SUPPORTED_EXTENSIONS = new Set([
  // Models
  ...MODEL_FILE_EXTENSIONS,
  // Textures
  'png', 'jpg', 'jpeg', 'webp',
  // HDRI
  'hdr', 'exr',
  // Video
  'mp4', 'webm',
  // Audio
  'mp3', 'wav',
  // Voxel
  'vox',
  // Source Code
  'ts', 'js', 'jsx', 'tsx',
  // JSON
  'json',
  // Binary
  'bin',
  // Scene
  ENGINE.SCENE_NAME_EXT.startsWith('.') ? ENGINE.SCENE_NAME_EXT.slice(1) : ENGINE.SCENE_NAME_EXT,
  // Mesh Combination
  MESH_COMBINATION_FILE_EXT.startsWith('.') ? MESH_COMBINATION_FILE_EXT.slice(1) : MESH_COMBINATION_FILE_EXT,
  // NavMesh
  ENGINE.NAVMESH_EXT.startsWith('.') ? ENGINE.NAVMESH_EXT.slice(1) : ENGINE.NAVMESH_EXT,
  // Lightmap build-data zip
  ENGINE.BUILD_DATA_ZIP_EXT.startsWith('.') ? ENGINE.BUILD_DATA_ZIP_EXT.slice(1) : ENGINE.BUILD_DATA_ZIP_EXT,
]);

/**
 * Check if a file extension is supported for import
 */
export function isSupportedFileExtension(extension: string): boolean {
  return SUPPORTED_EXTENSIONS.has(extension.toLowerCase());
}

/**
 * Determine the type of an asset based on its file extension and name
 */
/** Optimized asset type detection for performance and clarity */
export const getAssetType = (file: FileItem | IAssetFile): AssetType => {
  if (file.isDirectory) return AssetType.Folder;
  if (file.path.includes(JS_CLASSES_DIR_NAME)) return AssetType.JsClass;

  const name = file.name;
  const lowerName = name.toLowerCase();
  const ext = name.lastIndexOf('.') !== -1 ? name.slice(name.lastIndexOf('.') + 1).toLowerCase() : '';

  // Fast extension-based lookup
  if (isModelFileExtension(ext)) return AssetType.Model;
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'webp') return AssetType.Texture;
  if (ext === 'hdr' || ext === 'exr') return AssetType.HDRI;
  if (ext === 'mp4' || ext === 'webm') return AssetType.Video;
  if (ext === 'mp3' || ext === 'wav') return AssetType.Audio;
  if (ext === 'vox') return AssetType.Voxel;
  if (lowerName.endsWith(ENGINE.BUILD_DATA_ZIP_EXT)) return AssetType.LightmapBuildData;
  if (ext === ENGINE.SCENE_NAME_EXT.slice(1)) return AssetType.Scene;
  if (ext === 'ts' || ext === 'js' || ext === 'jsx' || ext === 'tsx') return AssetType.SourceCode;
  if (ext === MESH_COMBINATION_FILE_EXT.slice(1)) return AssetType.MeshCombination;
  if (ext === ENGINE.NAVMESH_EXT.slice(1)) return AssetType.NavMesh;

  // JSON and special compound extensions
  if (ext === 'json') {
    if (lowerName.endsWith(ENGINE.PREFAB_EXTENSION)) return AssetType.Prefab;
    if (lowerName.endsWith(ENGINE.MATERIAL_EXTENSION)) return AssetType.Material;
    if (lowerName.endsWith(ENGINE.VFX_EXTENSION)) return AssetType.VFX;
    if (lowerName.endsWith('.anim.json')) return AssetType.AnimationConfig;
    if (lowerName.endsWith('.skeleton-profile.json')) return AssetType.SkeletonProfile;

    // Check for resource files with any prefix pattern (*.*.json where * is not a known type)
    // This allows .res.json, .material.json (custom), .shader.json, etc.
    // Runs after specific checks to avoid false positives
    const dotCount = (lowerName.match(/\./g) || []).length;
    if (dotCount >= 2) {
      // Has at least 2 dots, check if it's a resource pattern
      const beforeJson = lowerName.slice(0, -5); // Remove '.json'
      const lastDotPos = beforeJson.lastIndexOf('.');
      if (lastDotPos > 0) {
        // Pattern like "name.prefix.json" - treat as resource
        return AssetType.Resource;
      }
    }
    return AssetType.Json;
  }

  return AssetType.Json;
};

/**
 * Interface for handling different types of assets
 */
export interface AssetHandler extends DragDropHandler {
  /**
   * Process the asset when opened
   */
  handleOpen(asset: IAssetFile): Promise<void>;

  /**
   * Process the asset when it is clicked
   */
  handleSelect(asset: IAssetFile): Promise<void>;

  /**
   * Process the asset when it is drag-and-dropped
   */
  // handleDrop(asset: IAssetFile, point: THREE.Vector3): Promise<void>;

  getTargetObject(): unknown;
}
